import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { monitoringAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import TrackingSnippetTabs, { IntegrationData } from '../../components/integration/TrackingSnippetTabs';

const IntegrationPage: React.FC = () => {
  useDocumentTitle('Integration');
  const { user, currentAsset } = useAuth();
  const [loading, setLoading] = useState(true);
  const [integrationData, setIntegrationData] = useState<IntegrationData | null>(null);
  const [activeTab, setActiveTab] = useState<'javascript' | 'api' | 'server' | 'mobile'>('javascript');

  useEffect(() => {
    if (currentAsset) {
      loadIntegrationData();
    }
  }, [currentAsset]);

  const loadIntegrationData = async () => {
    if (!currentAsset) return;

    setLoading(true);
    try {
      const response = await monitoringAPI.getIntegrationCode(currentAsset.id);
      if (response.data.status === 'success') {
        setIntegrationData(response.data.data);
      }
    } catch (error: any) {
      console.error('Failed to load integration data:', error);
      // Use current asset data as fallback
      setIntegrationData({
        tracking_id: currentAsset.tracking_id,
        api_key: currentAsset.api_key,
        integration_script_url: `${process.env.REACT_APP_API_URL}/v1/integration/${user?.current_org_id}/${currentAsset.tracking_id}/cloudayn.js`,
        integration_code: `<!-- CloudAyn Analytics -->
<script>
  (function(c,l,o,u,d,a,y,n){
    c[d]=c[d]||function(){(c[d].q=c[d].q||[]).push(arguments)};
    a=l.createElement(o);y=l.getElementsByTagName(o)[0];
    a.async=1;a.src=u;y.parentNode.insertBefore(a,y);
  })(window,document,'script','${`${process.env.REACT_APP_API_URL}/v1/integration/${user?.current_org_id}/${currentAsset.tracking_id}/cloudayn.js`}','cloudayn');

  cloudayn('init', '${currentAsset.tracking_id}');
</script>`,
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading integration data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!integrationData) {
    return (
      <DashboardLayout>
        <div className="alert alert-danger">Failed to load integration data</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="mb-4">
          <h1 className="h3 mb-1">Integration</h1>
          <p className="text-muted mb-0">
            Integrate CloudAyn with your asset: <strong>{currentAsset?.name}</strong>
          </p>
        </div>

        {/* Asset Info Card */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm bg-primary bg-opacity-10 border-primary">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center gap-3">
                      <div className="bg-primary bg-opacity-10 rounded p-3">
                        <i className="bi bi-box-seam text-primary fs-4"></i>
                      </div>
                      <div>
                        <div className="text-muted small">Current Asset</div>
                        <div className="fw-semibold fs-5">{currentAsset?.name}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center gap-3">
                      <div className="bg-primary bg-opacity-10 rounded p-3">
                        <i className="bi bi-fingerprint text-primary fs-4"></i>
                      </div>
                      <div>
                        <div className="text-muted small">Tracking ID</div>
                        <div className="fw-semibold font-monospace">{integrationData.tracking_id}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Integration Tabs */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom">
                <ul className="nav nav-tabs card-header-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'javascript' ? 'active' : ''}`}
                      onClick={() => setActiveTab('javascript')}
                    >
                      <i className="bi bi-code-slash me-2"></i>
                      JavaScript Integration
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'api' ? 'active' : ''}`}
                      onClick={() => setActiveTab('api')}
                    >
                      <i className="bi bi-key me-2"></i>
                      API Key
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'server' ? 'active' : ''}`}
                      onClick={() => setActiveTab('server')}
                    >
                      <i className="bi bi-terminal me-2"></i>
                      Server-Side Tracking
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'mobile' ? 'active' : ''}`}
                      onClick={() => setActiveTab('mobile')}
                    >
                      <i className="bi bi-phone me-2"></i>
                      Mobile App
                    </button>
                  </li>
                </ul>
              </div>

              <div className="card-body">
                {/* JavaScript Integration Tab */}
                {activeTab === 'javascript' && (
                  <TrackingSnippetTabs integrationData={integrationData} />
                )}

                {/* API Key Tab */}
                {activeTab === 'api' && (
                  <div className="integration-tab">
                    <h5 className="mb-3">API Authentication</h5>
                    <p className="text-muted">
                      Use this API key to authenticate requests to the CloudAyn API. Keep it secure and never expose it in client-side code.
                    </p>

                    <div className="mb-4">
                      <label className="form-label fw-semibold">API Key</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control font-monospace"
                          value={integrationData.api_key}
                          readOnly
                        />
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => copyToClipboard(integrationData.api_key, 'API Key')}
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-semibold">Example cURL Request</label>
                      <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <pre className="mb-0 text-light">
                          <code>{`curl -X GET "${process.env.REACT_APP_API_URL}/v1/analytics/dashboard/${currentAsset?.id}" \\
  -H "Authorization: Bearer ${integrationData.api_key}" \\
  -H "Content-Type: application/json"`}</code>
                        </pre>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-semibold">Example JavaScript Request</label>
                      <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <pre className="mb-0 text-light">
                          <code>{`fetch('${process.env.REACT_APP_API_URL}/v1/analytics/dashboard/${currentAsset?.id}', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer ${integrationData.api_key}',
    'Content-Type': 'application/json'
  }
})
  .then(response => response.json())
  .then(data => console.log(data));`}</code>
                        </pre>
                      </div>
                    </div>

                    <div className="alert alert-warning mb-0">
                      <i className="bi bi-shield-exclamation me-2"></i>
                      <strong>Security Warning:</strong> Never expose your API key in client-side code or public repositories.
                      Use environment variables and server-side requests to keep your API key secure.
                    </div>
                  </div>
                )}

                {/* Server-Side Tracking Tab */}
                {/* Mobile App Tab */}
                {activeTab === 'mobile' && (
                  <div className="integration-tab">
                    <h5 className="mb-2">Mobile App (iOS, Android, React Native, Flutter)</h5>
                    <p className="text-muted">
                      Answer a few questions about your app's flow and CloudAyn generates the tracking plan, a public write-only app key,
                      SDK code for every lifecycle step (splash, registration, add to cart, purchase…) and live verification.
                      The SDKs never block or crash your app — if CloudAyn is unreachable, events are queued on the device and sent later.
                    </p>
                    <div className="d-flex gap-2 flex-wrap">
                      <Link to="/app-analytics/setup" className="btn btn-primary"><i className="bi bi-magic me-1"></i>Open the app setup wizard</Link>
                      <Link to="/docs/app-integration" className="btn btn-outline-primary"><i className="bi bi-journal-code me-1"></i>Integration docs & REST API</Link>
                      <Link to="/app-analytics/overview" className="btn btn-outline-secondary"><i className="bi bi-graph-up me-1"></i>App analytics</Link>
                    </div>
                  </div>
                )}

                {activeTab === 'server' && (
                  <div className="integration-tab">
                    <h5 className="mb-3">Server-Side Event Tracking</h5>
                    <p className="text-muted">
                      Track events from your backend server using cURL, PHP, Node.js, or any HTTP client.
                      This is useful for tracking server-side actions like registrations, purchases, and other conversions.
                    </p>

                    <div className="alert alert-info mb-4">
                      <i className="bi bi-info-circle me-2"></i>
                      <strong>IP Address Tracking:</strong> When you send events from your server, the user's IP address is automatically captured.
                      This helps identify if multiple events (e.g., registrations and purchases) are from the same user.
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-semibold">API Key (for authentication)</label>
                      <div className="input-group mb-3">
                        <input
                          type="text"
                          className="form-control font-monospace"
                          value={integrationData.api_key}
                          readOnly
                        />
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => copyToClipboard(integrationData.api_key, 'API Key')}
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </div>
                    </div>

                    <h6 className="fw-semibold mt-4 mb-3">cURL Examples</h6>

                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label fw-semibold mb-0">Track Registration Event</label>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => copyToClipboard(`curl -X POST "${process.env.REACT_APP_API_URL}/v1/track-event" \\
  -H "X-API-Key: ${integrationData.api_key}" \\
  -H "Content-Type: application/json" \\
  -H "X-Forwarded-For: \${USER_IP}" \\
  -d '{
    "event_name": "registration",
    "event_category": "conversion",
    "visitor_id": "user_123",
    "session_id": "session_456",
    "properties": {
      "plan": "premium",
      "source": "landing_page"
    }
  }'`, 'cURL registration example')}
                        >
                          <i className="bi bi-clipboard me-2"></i>
                          Copy
                        </button>
                      </div>
                      <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <pre className="mb-0 text-light">
                          <code>{`curl -X POST "${process.env.REACT_APP_API_URL}/v1/track-event" \\
  -H "X-API-Key: ${integrationData.api_key}" \\
  -H "Content-Type: application/json" \\
  -H "X-Forwarded-For: \${USER_IP}" \\
  -d '{
    "event_name": "registration",
    "event_category": "conversion",
    "visitor_id": "user_123",
    "session_id": "session_456",
    "properties": {
      "plan": "premium",
      "source": "landing_page"
    }
  }'`}</code>
                        </pre>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label fw-semibold mb-0">Track Purchase Event (with amount)</label>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => copyToClipboard(`curl -X POST "${process.env.REACT_APP_API_URL}/v1/track-event" \\
  -H "X-API-Key: ${integrationData.api_key}" \\
  -H "Content-Type: application/json" \\
  -H "X-Forwarded-For: \${USER_IP}" \\
  -d '{
    "event_name": "purchase",
    "event_category": "conversion",
    "event_value": 99.99,
    "visitor_id": "user_123",
    "session_id": "session_456",
    "properties": {
      "order_id": "ORD-12345",
      "product": "Premium Plan",
      "currency": "USD"
    }
  }'`, 'cURL purchase example')}
                        >
                          <i className="bi bi-clipboard me-2"></i>
                          Copy
                        </button>
                      </div>
                      <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <pre className="mb-0 text-light">
                          <code>{`curl -X POST "${process.env.REACT_APP_API_URL}/v1/track-event" \\
  -H "X-API-Key: ${integrationData.api_key}" \\
  -H "Content-Type: application/json" \\
  -H "X-Forwarded-For: \${USER_IP}" \\
  -d '{
    "event_name": "purchase",
    "event_category": "conversion",
    "event_value": 99.99,
    "visitor_id": "user_123",
    "session_id": "session_456",
    "properties": {
      "order_id": "ORD-12345",
      "product": "Premium Plan",
      "currency": "USD"
    }
  }'`}</code>
                        </pre>
                      </div>
                    </div>

                    <h6 className="fw-semibold mt-4 mb-3">PHP Example</h6>
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label fw-semibold mb-0">Track Event with PHP</label>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => copyToClipboard(`<?php
function trackCloudaynEvent($eventName, $eventValue = null, $properties = []) {
    $apiKey = '${integrationData.api_key}';
    $apiUrl = '${process.env.REACT_APP_API_URL}/v1/track-event';

    // Get user's IP address
    $userIp = $_SERVER['HTTP_X_FORWARDED_FOR'] ?? $_SERVER['REMOTE_ADDR'] ?? '';

    $data = [
        'event_name' => $eventName,
        'event_category' => 'conversion',
        'visitor_id' => $_SESSION['user_id'] ?? uniqid('visitor_'),
        'session_id' => session_id(),
        'properties' => $properties
    ];

    if ($eventValue !== null) {
        $data['event_value'] = $eventValue;
    }

    $ch = curl_init($apiUrl);
    curl_setopt_array($ch, [
        CURLOPT_POST => true,
        CURLOPT_POSTFIELDS => json_encode($data),
        CURLOPT_HTTPHEADER => [
            'Content-Type: application/json',
            'X-API-Key: ' . $apiKey,
            'X-Forwarded-For: ' . $userIp
        ],
        CURLOPT_RETURNTRANSFER => true
    ]);

    $response = curl_exec($ch);
    curl_close($ch);

    return json_decode($response, true);
}

// Usage examples:
trackCloudaynEvent('registration', null, ['plan' => 'premium']);
trackCloudaynEvent('purchase', 99.99, ['order_id' => 'ORD-12345']);
?>`, 'PHP example')}
                        >
                          <i className="bi bi-clipboard me-2"></i>
                          Copy
                        </button>
                      </div>
                      <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <pre className="mb-0 text-light">
                          <code>{`<?php
function trackCloudaynEvent($eventName, $eventValue = null, $properties = []) {
    $apiKey = '${integrationData.api_key}';
    $apiUrl = '${process.env.REACT_APP_API_URL}/v1/track-event';

    // Get user's IP address
    $userIp = $_SERVER['HTTP_X_FORWARDED_FOR'] ?? $_SERVER['REMOTE_ADDR'] ?? '';

    $data = [
        'event_name' => $eventName,
        'event_category' => 'conversion',
        'visitor_id' => $_SESSION['user_id'] ?? uniqid('visitor_'),
        'session_id' => session_id(),
        'properties' => $properties
    ];

    if ($eventValue !== null) {
        $data['event_value'] = $eventValue;
    }

    $ch = curl_init($apiUrl);
    curl_setopt_array($ch, [
        CURLOPT_POST => true,
        CURLOPT_POSTFIELDS => json_encode($data),
        CURLOPT_HTTPHEADER => [
            'Content-Type: application/json',
            'X-API-Key: ' . $apiKey,
            'X-Forwarded-For: ' . $userIp
        ],
        CURLOPT_RETURNTRANSFER => true
    ]);

    $response = curl_exec($ch);
    curl_close($ch);

    return json_decode($response, true);
}

// Usage examples:
trackCloudaynEvent('registration', null, ['plan' => 'premium']);
trackCloudaynEvent('purchase', 99.99, ['order_id' => 'ORD-12345']);
?>`}</code>
                        </pre>
                      </div>
                    </div>

                    <h6 className="fw-semibold mt-4 mb-3">Node.js Example</h6>
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label fw-semibold mb-0">Track Event with Node.js</label>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => copyToClipboard(`const axios = require('axios');

const CLOUDAYN_API_KEY = '${integrationData.api_key}';
const CLOUDAYN_API_URL = '${process.env.REACT_APP_API_URL}/v1/track-event';

async function trackCloudaynEvent(eventName, eventValue, properties, userIp) {
  try {
    const response = await axios.post(CLOUDAYN_API_URL, {
      event_name: eventName,
      event_category: 'conversion',
      event_value: eventValue,
      visitor_id: properties.userId || \`visitor_\${Date.now()}\`,
      session_id: properties.sessionId || \`session_\${Date.now()}\`,
      properties: properties
    }, {
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': CLOUDAYN_API_KEY,
        'X-Forwarded-For': userIp
      }
    });

    return response.data;
  } catch (error) {
    console.error('CloudAyn tracking error:', error.message);
    throw error;
  }
}

// Express.js middleware example
app.post('/api/purchase', async (req, res) => {
  const userIp = req.headers['x-forwarded-for'] || req.ip;

  // Process purchase...
  const order = await processPurchase(req.body);

  // Track the purchase event
  await trackCloudaynEvent('purchase', order.total, {
    userId: req.user.id,
    sessionId: req.sessionID,
    order_id: order.id,
    product: order.productName
  }, userIp);

  res.json({ success: true, order });
});`, 'Node.js example')}
                        >
                          <i className="bi bi-clipboard me-2"></i>
                          Copy
                        </button>
                      </div>
                      <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                        <pre className="mb-0 text-light">
                          <code>{`const axios = require('axios');

const CLOUDAYN_API_KEY = '${integrationData.api_key}';
const CLOUDAYN_API_URL = '${process.env.REACT_APP_API_URL}/v1/track-event';

async function trackCloudaynEvent(eventName, eventValue, properties, userIp) {
  try {
    const response = await axios.post(CLOUDAYN_API_URL, {
      event_name: eventName,
      event_category: 'conversion',
      event_value: eventValue,
      visitor_id: properties.userId || \`visitor_\${Date.now()}\`,
      session_id: properties.sessionId || \`session_\${Date.now()}\`,
      properties: properties
    }, {
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': CLOUDAYN_API_KEY,
        'X-Forwarded-For': userIp
      }
    });

    return response.data;
  } catch (error) {
    console.error('CloudAyn tracking error:', error.message);
    throw error;
  }
}

// Express.js middleware example
app.post('/api/purchase', async (req, res) => {
  const userIp = req.headers['x-forwarded-for'] || req.ip;

  // Process purchase...
  const order = await processPurchase(req.body);

  // Track the purchase event
  await trackCloudaynEvent('purchase', order.total, {
    userId: req.user.id,
    sessionId: req.sessionID,
    order_id: order.id,
    product: order.productName
  }, userIp);

  res.json({ success: true, order });
});`}</code>
                        </pre>
                      </div>
                    </div>

                    <div className="alert alert-success mb-0">
                      <i className="bi bi-check-circle me-2"></i>
                      <strong>Best Practice:</strong> Use consistent <code>visitor_id</code> values (e.g., user ID from your database)
                      to link events across different sessions. Combined with IP tracking, this helps identify user behavior patterns.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Integration Guides */}
        <div className="row mt-4">
          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h6 className="fw-semibold mb-3">
                  <i className="bi bi-book text-primary me-2"></i>
                  Platform-Specific Guides
                </h6>
                <ul className="list-unstyled mb-0">
                  <li className="mb-2">
                    <i className="bi bi-chevron-right text-muted me-2"></i>
                    <a href="#" className="text-decoration-none">WordPress Integration</a>
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-chevron-right text-muted me-2"></i>
                    <a href="#" className="text-decoration-none">React/Next.js Integration</a>
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-chevron-right text-muted me-2"></i>
                    <a href="#" className="text-decoration-none">Vue.js Integration</a>
                  </li>
                  <li>
                    <i className="bi bi-chevron-right text-muted me-2"></i>
                    <a href="#" className="text-decoration-none">Angular Integration</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h6 className="fw-semibold mb-3">
                  <i className="bi bi-lightbulb text-warning me-2"></i>
                  Quick Tips
                </h6>
                <ul className="mb-0 ps-3">
                  <li className="mb-2">Install the script on all pages you want to track</li>
                  <li className="mb-2">Test your integration using browser developer tools</li>
                  <li className="mb-2">Check the Network tab for successful API calls</li>
                  <li>Contact support if you don't see data after 15 minutes</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default IntegrationPage;
